import React, { useState } from 'react';
import { Button, Flex, Space, Tooltip } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  TelegramMessageButtonEntity,
  isSafeExternalUrl,
  isTelegramJoinLink,
} from '../../../domain/entities/TelegramMessenger';

import './MessengerMessageButtons.styles.css';

interface MessengerMessageButtonsProps {
  buttons: TelegramMessageButtonEntity[][];
  canSendText: boolean;
  onPress: (button: TelegramMessageButtonEntity) => Promise<void>;
  onSendText: (text: string) => Promise<void>;
  onJoinLink: (url: string) => Promise<void>;
}

const MessengerMessageButtons: React.FC<MessengerMessageButtonsProps> = ({ buttons, canSendText, onPress, onSendText, onJoinLink }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<string>();

  const run = async (id: string, action: () => Promise<void>) => {
    setBusy(id);
    try {
      await action();
    } finally {
      setBusy(undefined);
    }
  };

  const renderButton = (button: TelegramMessageButtonEntity, id: string) => {
    if (button.kind === 'callback') {
      return (
        <Button key={id} size="small" loading={busy === id} disabled={!!busy && busy !== id} onClick={() => run(id, () => onPress(button))}>
          {button.text}
        </Button>
      );
    }

    if (button.kind === 'text') {
      return (
        <Button key={id} size="small" loading={busy === id} disabled={!canSendText || (!!busy && busy !== id)} onClick={() => run(id, () => onSendText(button.text))}>
          {button.text}
        </Button>
      );
    }

    if (button.kind === 'url' && isSafeExternalUrl(button.url)) {
      const link = (
        <Button size="small" href={button.url} target="_blank" rel="noopener noreferrer" icon={<ExportOutlined />} iconPosition="end">
          {button.text}
        </Button>
      );
      if (!isTelegramJoinLink(button.url)) return <React.Fragment key={id}>{link}</React.Fragment>;
      return (
        <Space.Compact key={id} size="small">
          {link}
          <Button size="small" type="primary" loading={busy === id} disabled={!!busy && busy !== id} onClick={() => run(id, () => onJoinLink(button.url as string))}>
            {t('messenger-join', { ns: 'buttons' })}
          </Button>
        </Space.Compact>
      );
    }

    return (
      <Tooltip key={id} title={t('messenger-button-unsupported', { ns: 'labels' })}>
        <Button size="small" disabled>{button.text}</Button>
      </Tooltip>
    );
  };

  return (
    <Flex vertical gap={4} className="messenger-message-buttons">
      {buttons.map((row, rowIndex) => (
        <Flex key={rowIndex} gap={4} wrap="wrap">
          {row.map((button, buttonIndex) => renderButton(button, `${rowIndex}:${buttonIndex}`))}
        </Flex>
      ))}
    </Flex>
  );
};

export default MessengerMessageButtons;
